// Pure helpers for the App sidebar group/order computation.
// Extracted from App.tsx so the demo-mode pinning rule (Task #535) and
// the persona-priority sort can be unit-tested without rendering the
// full React tree.
//
// Demo-mode rule (Task #535):
//   1. Overview comes first
//   2. Management second
//   3. Active persona workspace groups (Finance / HR / Legal / SA) follow
//   4. Everything else (Compliance, Library, ...) keeps its original order
//
// Non-demo rule:
//   - Persona-priority sort: groups whose items appear earliest in the
//     active persona's PERSONA_NAV_ORDER bubble to the top, original
//     NAV_ITEMS index breaks ties.
//   - When no persona is active, original NAV_ITEMS order is preserved.

export type Persona = "Finance" | "HR" | "Legal" | "AD" | "SA";

export interface SidebarItemLike {
  href: string;
  label: string;
}

export interface SidebarGroupLike<T extends SidebarItemLike = SidebarItemLike> {
  label: string;
  items: T[];
}

export interface PersonaGroupDef {
  persona: Persona;
  label: string;
  hrefs: string[];
}

export interface ComputeSidebarGroupsInput<T extends SidebarItemLike> {
  navItems: SidebarGroupLike<T>[];
  personaGroupDefs: PersonaGroupDef[];
  personaNavOrder: Record<Persona, string[]>;
  effectivePersona: Persona | null;
  isAdmin: boolean;
  isDemoMode: boolean;
}

export function computeSidebarGroups<T extends SidebarItemLike>(
  input: ComputeSidebarGroupsInput<T>,
): SidebarGroupLike<T>[] {
  const { navItems, personaGroupDefs, personaNavOrder, effectivePersona, isAdmin, isDemoMode } = input;

  const visiblePersonaGroups = personaGroupDefs.filter(
    (g) => isAdmin || effectivePersona === g.persona,
  );
  const movedHrefs = new Set(visiblePersonaGroups.flatMap((g) => g.hrefs));
  const allItems: T[] = navItems.flatMap((g) => g.items);
  const findItem = (href: string): T | undefined => allItems.find((it) => it.href === href);

  const personaGroups: SidebarGroupLike<T>[] = visiblePersonaGroups
    .map((g) => ({
      label: g.label,
      items: g.hrefs.map(findItem).filter((it): it is T => Boolean(it)),
    }))
    .filter((g) => g.items.length > 0);

  // Strip moved hrefs from their original groups so no item appears
  // twice (Task #535 acceptance: "no item appears twice"). Empty groups
  // are filtered out so the sidebar doesn't render an empty header.
  const strippedBase: SidebarGroupLike<T>[] = navItems
    .map((g) => ({
      ...g,
      items: g.items.filter((it) => !movedHrefs.has(it.href)),
    }))
    .filter((g) => g.items.length > 0);

  if (isDemoMode) {
    const overview = strippedBase.find((g) => g.label === "Overview");
    const management = strippedBase.find((g) => g.label === "Management");
    const rest = strippedBase.filter(
      (g) => g.label !== "Overview" && g.label !== "Management",
    );
    return [
      ...(overview ? [overview] : []),
      ...(management ? [management] : []),
      ...personaGroups,
      ...rest,
    ];
  }

  const baseItems: SidebarGroupLike<T>[] = [...personaGroups, ...strippedBase];
  if (!effectivePersona) return baseItems;
  const order = personaNavOrder[effectivePersona];
  const itemPriority = (href: string) => {
    const idx = order.indexOf(href);
    return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
  };
  const groupPriority = (g: SidebarGroupLike<T>) =>
    Math.min(...g.items.map((i) => itemPriority(i.href)));
  return [...baseItems]
    .map((g, i) => ({ g, i, p: groupPriority(g) }))
    .sort((a, b) => a.p - b.p || a.i - b.i)
    .map((x) => x.g);
}
